import { useQuery, useQueryClient } from '@tanstack/react-query'
import { NotFoundErrorCode, OfferErrorCode, OfferStatus, ValidationErrorCode } from '@today/types'
import { isAxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { type OfferDTO, type OfferMessagePatch, offerService } from '@/services/offer.service'
import { localeOptions } from './locale-options'
import { httpLocalePort, type LocalePort } from './locale-port'

/** The wait after the last edit before the preview re-renders (TO-124 REQ-007). */
export const PREVIEW_DELAY_MS = 300
/** The wait after the last edit before the draft is autosaved (TO-124 REQ-008). */
export const AUTOSAVE_DELAY_MS = 1000
/** The longest message the offer stores. */
export const MESSAGE_MAX = 2000

const NOT_EDITABLE = 'This offer has already been sent and can no longer be edited.'
const TEXT_MISSING = 'A required legal text is missing for this language.'
const NOT_FOUND = 'This offer could not be found.'
const SAVING = 'Saving…'
const SAVED = 'All changes saved'

interface ApiErrorBody {
	error?: { code?: string }
}

const codeOf = (error: unknown): string | undefined =>
	isAxiosError<ApiErrorBody>(error) ? error.response?.data?.error?.code : undefined

/** The preview pane's message for a refused preview (TO-124 contract table). */
export const previewErrorOf = (error: unknown): string => {
	switch (codeOf(error)) {
		case ValidationErrorCode.TEXT_NOT_AVAILABLE:
			return TEXT_MISSING
		case NotFoundErrorCode.OFFER_NOT_FOUND:
			return NOT_FOUND
		default:
			return 'The preview could not be rendered.'
	}
}

/** The save indicator's message for a refused autosave (TO-124 contract table). */
export const saveErrorOf = (error: unknown): string => {
	switch (codeOf(error)) {
		case ValidationErrorCode.TEXT_NOT_AVAILABLE:
			return TEXT_MISSING
		case OfferErrorCode.OFFER_TRANSITION_NOT_ALLOWED:
		case OfferErrorCode.OFFER_STATUS_CONFLICT:
		case OfferErrorCode.OFFER_SEND_IN_PROGRESS:
			return NOT_EDITABLE
		case NotFoundErrorCode.OFFER_NOT_FOUND:
			return NOT_FOUND
		default:
			return 'The message could not be saved. Try again.'
	}
}

/** The editor's text as the offer stores it: blank (empty or whitespace-only) is no message. */
const toIntroduction = (message: string): string | null => (message.trim() ? message : null)

interface Draft {
	locale: string
	message: string
}

interface Saved {
	locale: string
	introduction: string | null
}

/** The fields of `draft` that differ from what is saved — the autosave body. */
const changesOf = (draft: Draft, saved: Saved): OfferMessagePatch => {
	const introduction = toIntroduction(draft.message)
	return {
		...(introduction === toIntroduction(saved.introduction ?? '') ? {} : { introduction }),
		...(draft.locale === saved.locale ? {} : { locale: draft.locale }),
	}
}

const savedOf = (offer: OfferDTO): Saved => ({ locale: offer.locale, introduction: offer.introduction })

/** The preview pane's state: renders a draft, showing only the latest response. */
function useLivePreview(offerId: string) {
	const [html, setHtml] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)
	const seqRef = useRef(0)
	const run = (current: Draft) => {
		const seq = ++seqRef.current
		offerService
			.preview(offerId, { locale: current.locale, introduction: toIntroduction(current.message) })
			.then((rendered) => {
				if (seq !== seqRef.current) return
				setHtml(rendered)
				setError(null)
			})
			.catch((refusal: unknown) => {
				if (seq === seqRef.current) setError(previewErrorOf(refusal))
			})
	}
	return { html, error, run }
}

/**
 * The autosave: PATCHes a draft's changes against the last saved values, with a status.
 * One PATCH at a time — a draft due while one is in flight is held and saved (latest
 * only) once it settles, so the newest draft is always the last write.
 */
function useAutosave(offerId: string) {
	const client = useQueryClient()
	const [status, setStatus] = useState<string | null>(null)
	const savedRef = useRef<Saved | null>(null)
	const inFlightRef = useRef(false)
	const heldRef = useRef<Draft | null>(null)
	const settle = () => {
		inFlightRef.current = false
		const held = heldRef.current
		heldRef.current = null
		if (held) run(held)
	}
	const run = (current: Draft) => {
		const saved = savedRef.current
		if (!saved) return
		if (inFlightRef.current) {
			heldRef.current = current
			return
		}
		const patch = changesOf(current, saved)
		if (Object.keys(patch).length === 0) return
		inFlightRef.current = true
		setStatus(SAVING)
		offerService
			.update(offerId, patch)
			.then((updated) => {
				savedRef.current = savedOf(updated)
				client.setQueryData(['offer', offerId], updated)
				setStatus(SAVED)
			})
			.catch((refusal: unknown) => setStatus(saveErrorOf(refusal)))
			.finally(settle)
	}
	return { status, run, seed: (offer: OfferDTO) => (savedRef.current = savedOf(offer)) }
}

/** Two restartable timers: the preview's and the autosave's. */
function useDebouncedPair(onPreview: (d: Draft) => void, onSave: (d: Draft) => void) {
	const previewTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
	const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
	useEffect(
		() => () => {
			clearTimeout(previewTimer.current)
			clearTimeout(saveTimer.current)
		},
		[],
	)
	return (next: Draft) => {
		clearTimeout(previewTimer.current)
		previewTimer.current = setTimeout(() => onPreview(next), PREVIEW_DELAY_MS)
		clearTimeout(saveTimer.current)
		saveTimer.current = setTimeout(() => onSave(next), AUTOSAVE_DELAY_MS)
	}
}

/**
 * Offer Wizard Step 3 (TO-124): the offer's message and locale being edited, a live
 * preview of the landing re-rendered {@link PREVIEW_DELAY_MS} after the last edit
 * with the unsaved values (only the latest response is shown), and an autosave of
 * the changed fields {@link AUTOSAVE_DELAY_MS} after the last edit. A non-DRAFT offer
 * is read-only: edits are ignored and nothing is saved.
 */
export function useOfferMessageStep(offerId: string, port: LocalePort = httpLocalePort) {
	const { tenantSlug } = useViewerPermissions()
	const offer = useQuery<OfferDTO>({ queryKey: ['offer', offerId], queryFn: () => offerService.get(offerId) })
	const locales = useQuery<string[]>({
		queryKey: ['offer-wizard', 'locales', tenantSlug],
		queryFn: () => port.listEnabledLocales(tenantSlug),
	})
	const preview = useLivePreview(offerId)
	const autosave = useAutosave(offerId)
	const schedule = useDebouncedPair(preview.run, autosave.run)
	const [draft, setDraft] = useState<Draft | null>(null)
	const draftRef = useRef<Draft | null>(null)
	const readOnly = offer.data !== undefined && offer.data.status !== OfferStatus.DRAFT

	// Seed the editor once from the stored offer, and render its preview at once.
	// biome-ignore lint/correctness/useExhaustiveDependencies: seeds once per loaded offer; later refetches must not discard edits
	useEffect(() => {
		if (!offer.data || draftRef.current) return
		const initial = { locale: offer.data.locale, message: offer.data.introduction ?? '' }
		draftRef.current = initial
		autosave.seed(offer.data)
		setDraft(initial)
		preview.run(initial)
	}, [offer.data])

	const edit = (change: Partial<Draft>) => {
		if (readOnly || !draftRef.current) return
		const next = { ...draftRef.current, ...change }
		draftRef.current = next
		setDraft(next)
		schedule(next)
	}

	const locale = draft?.locale ?? offer.data?.locale ?? ''
	return {
		loading: offer.isPending || (offer.isSuccess && draft === null),
		loadFailed: offer.isError,
		readOnly,
		message: draft?.message ?? '',
		locale,
		localeOptions: locale ? localeOptions(locales.data ?? [], locale) : [],
		saveStatus: readOnly ? NOT_EDITABLE : autosave.status,
		previewHtml: preview.html,
		previewError: preview.error,
		setMessage: (message: string) => edit({ message }),
		setLocale: (next: string) => edit({ locale: next }),
	}
}
